// Book desk modules, row 3. The research layout's faces: the score histogram, the
// bench strip, the cadence timeline and the overlay bullets. The validation
// layout's: the mirror and the clean-inception note. The live layout's: the
// kill-entries switch, the live-cap rejections placeholder and the containment
// rows. Like the other rows, each face that reads is fed by the SAME TanStack
// query keys/options as the panel behind its drawer, so opening the drawer is a
// cache hit, and each is state-only: no buttons or links inside.
//
// What a face must never do: read an unknown as a zero, show an old figure as a
// current one, show an unknown as a reassuring state, or lose what it already
// has. A query that is loading (or paused with no data) shows the skeleton; one
// that failed with nothing to show gets the error rung. The error rung is
// `unavailable(q)`, never a bare `isError`: a background refetch that fails keeps
// the figures the face already has. A figure that is merely unknown (a read that
// failed beside one that did not) is a dash, never a zero. The live page is the
// containment surface for real-money trading, so its faces say "unavailable" or
// "not yet surfaced" where a number or a green word would be a guess.
//
// The research faces and the mirror read the clock (the log's age, the weekly
// re-score day, the window around today, the overlay's age, the mirror tick's
// age), and `useNow` re-renders them every CLOCK_MS. That is what makes an age
// work: when the engine stops writing the confidence log, or the overlay is
// untouched for days, every refetch returns the same rows, react-query keeps the
// same `data` reference, and nothing else would ever render the face again. The
// reads themselves are bookModuleHooks'.
import type { ReactNode } from 'react'
import { useNow } from '../../hooks/useNow'
import {
  addCalendarDays,
  daysUntil,
  etTodayISO,
  formatCadence,
  nextTuesdayISO,
  shortDate,
} from '../../lib/cadence'
import { relativeTime } from '../../lib/relativeTime'
import type { ConvictionScorecard, ConvictionStatus } from '../../types'
import { BulletBars } from '../viz/BulletBars'
import { Histogram } from '../viz/Histogram'
import { MiniFunnel } from '../viz/MiniFunnel'
import { StripPlot } from '../viz/StripPlot'
import { Timeline } from '../viz/Timeline'
import { Module } from './Module'
import { StatModule } from './StatModule'
import {
  CLOCK_MS,
  useBookDrawdown,
  useBookOverlay,
  useBookPositions,
  useBookRoster,
  useSectorRuns,
  useSourceConviction,
  type BookModuleProps,
} from './bookModuleHooks'
import {
  benchStrip,
  cadenceLanes,
  isStaleLog,
  liveGate,
  mirrorSummary,
  overlayBullets,
  scoreBins,
  scoreCaveat,
  valveSummary,
  type LiveGate,
  type ScoreCaveat,
} from './bookSummaries'
import { DASH, isDormant, num, stillLoading, unavailable } from './fleetModuleHooks'
import './BookModules.css'

/**
 * The research layout's faces take the grid span the page gives them (the scorecard is 2 on the research page
 * and 3 on the validation page); the default is the research layout's. The validation and live faces each sit
 * at one span and take none.
 */
type RowProps = BookModuleProps & { span?: number }

/** Keeps a short phrase ("· θ as of 10-15") on one line when the foot wraps. */
const together = (phrase: string) => phrase.replace(/ /g, '\u00a0')

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v)

// ---------------------------------------------------------- score histogram

/** The scores the histogram bins, and the scale the θ line sits on. */
const SCORE_DOMAIN: [number, number] = [0.5, 1]

/**
 * The latest confidence day's thesis-break scores in ten bins against the exit
 * valve's θ (ruling 24: a name fires at score >= θ, so the bins at or above the
 * line are the ones that would exit). A subscriber book has no conviction rows, so
 * every book with a pick source shows the RESEARCH book's scores and the title
 * says so (rulings 26, 38); the title follows where the figures come from, not the
 * role (as the valve's does), so a book with no source reads its own data and is
 * just "Scores". The foot always says what day the scores are from, in warn tone
 * once that is more than four days ago: the bars are real, only old.
 *
 * θ comes from the status read. Until it settles the face is loading (bars with no
 * line yet would look like nothing is near the threshold); if it fails, or carries
 * no θ, the bars are drawn with no line and the foot says θ is unavailable, since
 * a missing line alone reads the same way.
 */
export function ScorecardHistogramModule({ book, span }: RowProps) {
  const { statusQ, logQ, sourceless } = useSourceConviction(book)
  const now = useNow(CLOCK_MS)
  const today = etTodayISO(now)

  const { bins, names, logDate } = scoreBins(logQ.data)
  const { theta } = valveSummary(statusQ.data, undefined, logQ.data, today)
  const stale = isStaleLog(logDate, today)

  const scores = `${names} thesis-break ${names === 1 ? 'score' : 'scores'}`

  return (
    <Module
      title={book.pick_source_account_id != null ? 'Research scores' : 'Scores'}
      drawerKey="scorecard"
      span={span ?? 2}
      isLoading={!sourceless && (stillLoading(logQ) || stillLoading(statusQ))}
      isError={!sourceless && unavailable(logQ)}
      errorText="scores unavailable"
      isEmpty={sourceless || names === 0}
      emptyText={sourceless ? 'dormant · no pick source' : 'no confidence scores yet'}
    >
      {/* Histogram is sized in percent of its parent, so the parent needs a definite height (BookModules.css). */}
      <div className="book-hist">
        <Histogram
          bins={bins}
          domain={SCORE_DOMAIN}
          threshold={theta ?? undefined}
          thresholdLabel={theta === null ? undefined : `θ ${theta.toFixed(2)}`}
          ariaLabel={theta === null ? scores : `${scores}, θ ${theta.toFixed(2)}`}
        />
      </div>
      <p className={`module-foot${stale ? ' warn' : ''}`}>
        {`${names} ${names === 1 ? 'name' : 'names'} · as of ${logDate === null ? DASH : logDate.slice(5)}`}
        {theta === null && <span className="warn">{' '}{together('· θ unavailable')}</span>}
      </p>
    </Module>
  )
}

// -------------------------------------------------------------- bench strip

/** BenchPanel's BENCH_CAP: the size the bench count is shown against. */
const BENCH_CAP = 25

/**
 * The bench size exactly as BenchPanel prints it: `candidates_by_state.benched`
 * from the status, so the face and the drawer agree. The backend counts with a
 * GROUP BY, so a state with no candidates has no key: a status that carries the map
 * but no `benched` entry means an empty bench, and that is known. A status without
 * a readable map, or with a `benched` that is not a count, is null: unknown, never 0.
 */
function benchedCount(status: ConvictionStatus | undefined): number | null {
  const byState: unknown = status?.candidates_by_state
  if (typeof byState !== 'object' || byState === null || Array.isArray(byState)) return null
  const benched = (byState as Record<string, unknown>).benched
  if (benched === undefined) return 0
  const n = num(benched)
  return n !== null && n >= 0 ? n : null
}

/** How many names the scorecard lists as held; 0 when it carries no positions list. */
function heldCount(scorecard: ConvictionScorecard | undefined): number {
  const positions: unknown = scorecard?.positions
  return Array.isArray(positions) ? positions.filter((p) => isRecord(p) && p.state === 'held').length : 0
}

/**
 * The benched names on a trend-excess axis against the weakest name held (ruling
 * 25: the bench promotes strongest-first when a slot opens). The COUNT is the
 * status's, the dots are the roster's, and the two can differ: the roster is
 * capped, and a name with no trend excess has no place on the axis. When the
 * roster draws fewer names than the status counts, the headline says how many are
 * shown and the above-count is a lower bound. With no held name there is no
 * reference line, and the headline leads with the status count, never with the
 * (possibly zero) number of dots.
 *
 * Every read must have settled before the strip is drawn: a strip with no reference
 * line yet would flip its headline once the scorecard lands. The count in the header
 * is part of the strip, so it is there only when the strip is: over the loading, error
 * or empty rung it would put a figure on a face that has just said it has none. A
 * scorecard that FAILED, has no positions list (a JSON null body included), or lists
 * held names none of which has a trend excess is not a scorecard that holds nothing:
 * the missing "lowest held" line gets a warn marker in the headline, so it cannot be
 * read as "no name is held".
 *
 * The status and scorecard are the source book's (useSourceConviction); the bench is a
 * research-book face, where the source is the book itself.
 */
export function BenchModule({ book, span }: RowProps) {
  const rosterQ = useBookRoster(book)
  const { statusQ, scorecardQ } = useSourceConviction(book)
  const now = useNow(CLOCK_MS)

  const scorecard = scorecardQ.data
  const { points, lowestHeld, domain, above } = benchStrip(rosterQ.data, scorecard)
  const statusBenched = benchedCount(statusQ.data)
  const shown = points.length
  const heldUnknown =
    unavailable(scorecardQ) ||
    // `null?.positions`: a 200 with a null body is data (react-query forbids only undefined), and no list.
    (scorecard !== undefined && !Array.isArray(scorecard?.positions)) ||
    // Held names with no `sue` to draw: no reference line, but not an empty book.
    (lowestHeld === null && heldCount(scorecard) > 0)

  const loading = [rosterQ, scorecardQ, statusQ].some(stillLoading)
  const failed = unavailable(rosterQ) || statusBenched === null
  const drawn = !loading && !failed && statusBenched !== null && statusBenched > 0

  // With no reference line, or nothing to place against it, "N above lowest held" would be 0 or
  // unknowable: the status count leads, the same number the header and the drawer print.
  const lead = lowestHeld === null || shown === 0 ? `${statusBenched ?? shown} benched` : `${above} above lowest held`
  const headlineText =
    statusBenched !== null && shown < statusBenched ? `${lead} · ${shown} of ${statusBenched} shown` : lead

  return (
    <Module
      title="Bench"
      caption={drawn ? `${statusBenched}/${BENCH_CAP}` : undefined}
      drawerKey="bench"
      span={span ?? 2}
      isLoading={loading}
      isError={failed}
      errorText="bench unavailable"
      isEmpty={statusBenched === 0}
      emptyText="bench empty — fills as picks graduate past the 25 live slots"
    >
      <StripPlot
        points={points}
        domain={domain}
        lines={lowestHeld === null ? [] : [{ value: lowestHeld, label: 'lowest held' }]}
        headline={
          <>
            {headlineText}
            {heldUnknown && <span className="warn">{' · held '}{together('data unavailable')}</span>}
          </>
        }
        ariaLabel={`benched names by trend excess, ${headlineText}${heldUnknown ? ' · held data unavailable' : ''}`}
      />
      <p className="module-foot">{`next re-score ${shortDate(nextTuesdayISO(etTodayISO(now)))}`}</p>
    </Module>
  )
}

// ----------------------------------------------------------------- cadence

/** The window: three days of the past (so a date that just passed is still on it), then 14 days in all. */
const CADENCE_LOOKBACK_DAYS = 3
const CADENCE_DAYS = 14

/**
 * The caption is the next event's lane name and its day count, and the span-3 header has room for about 22
 * characters of it (measured: 235px beside "CADENCE" at 7.2px a character). "Bench re-score tomorrow" is 23,
 * 2.6px too wide, so every Monday it ended in an ellipsis. The bench lane is the one name long enough to
 * matter, and "Re-score" says the same beside the lane list the timeline draws.
 */
const CAPTION_NAME: Record<string, string> = { 'Bench re-score': 'Re-score' }

/**
 * The research cadence on a 14-day axis: discovery, the weekly bench re-score,
 * the incubating names' entry and stale-out dates, and the sector evaluator. The
 * roster is the anchor: the face is its loading and error rungs. The sector runs
 * only fill one lane, so while they load, or if they fail, that lane simply has no
 * events (the face never goes to an error for it), and a refetch that fails keeps
 * the runs it already has.
 *
 * A dot that is not done and sits left of the today line is OVERDUE: the entry or
 * the stale-out of a name that is still incubating has not happened. It is never
 * "complete" (cadenceLanes marks only what has happened as done), and the label
 * must not say so. The caption names the next event; it is left off until the
 * roster is in, because two of the lanes are calendar dates that would make a
 * caption out of a face that is showing nothing.
 *
 * Mount it at span 3: Timeline's label flip is tuned for that track (~181px).
 */
export function CadenceModule({ book, span }: RowProps) {
  const rosterQ = useBookRoster(book)
  const runsQ = useSectorRuns(book)
  const now = useNow(CLOCK_MS)
  const today = etTodayISO(now)
  const start = addCalendarDays(today, -CADENCE_LOOKBACK_DAYS)

  const { lanes, next } = cadenceLanes(rosterQ.data, runsQ.data, today, start, CADENCE_DAYS)
  const named = rosterQ.data !== undefined && next !== null ? next : null

  return (
    <Module
      title="Cadence"
      caption={
        named === null ? undefined : `${CAPTION_NAME[named.label] ?? named.label} ${formatCadence(daysUntil(named.date, today))}`
      }
      drawerKey="cadence"
      span={span ?? 3}
      isLoading={stillLoading(rosterQ)}
      isError={unavailable(rosterQ)}
      errorText="cadence unavailable"
    >
      <Timeline
        start={start}
        days={CADENCE_DAYS}
        today={today}
        lanes={lanes}
        ariaLabel={`research cadence, ${CADENCE_DAYS} days${named === null ? '' : `, next ${named.label} ${shortDate(named.date)}`}`}
      />
    </Module>
  )
}

// ----------------------------------------------------------------- overlay

/** A displayed value as it is spoken: a dash is "unknown". */
const spoken = (v: string) => (v === DASH ? 'unknown' : v)

/** The valve tile's caveat wording (BookModulesRow1), which the foot prefixes with θ. */
function caveatText(c: ScoreCaveat): string {
  if (c.kind === 'stale') return `as of ${c.logDate.slice(5)}`
  if (c.kind === 'coverage') return 'coverage unknown'
  return `${c.names}/${c.held} scored`
}

/**
 * Three overlay knobs, each as its live value against its setting (θ, the position
 * cap, the drawdown floor), and the two flags. The overlay read is the face: its
 * loading and error rungs. The four reads behind the live values fill their own
 * rows as they arrive, and a read that is missing is a dash in its row, never a 0.
 * A dormant book is never asked for its positions or drawdown floor (they 404 by
 * design), and a cached copy of either is not drawn.
 *
 * The θ row's live value is the highest score of the latest confidence day, so it
 * carries what the exit-valve tile's score carries (scoreCaveat: the one shared
 * precedence), as a warn note in the foot: the log is old (which day it is as of), or
 * fewer names were scored than are held (N/M), or the scorecard could not say how many
 * are held. The last is only said once the scorecard has settled: while it is in
 * flight the holdings are not yet known, which would flash a warning on every load.
 *
 * The entries flag is the effective one (overlayBullets).
 */
export function OverlayModule({ book, span }: RowProps) {
  const dormant = isDormant(book)
  const overlayQ = useBookOverlay(book)
  const { statusQ, scorecardQ, logQ, sourceless } = useSourceConviction(book)
  const positionsQ = useBookPositions(book)
  const drawdownQ = useBookDrawdown(book)
  const now = useNow(CLOCK_MS)
  const today = etTodayISO(now)

  // A sourceless dormant book has no conviction data anywhere (ruling 33): the reads are off, and a copy
  // an earlier run left in the cache is not drawn.
  const conviction = sourceless
    ? { status: undefined, scorecard: undefined, log: undefined }
    : { status: statusQ.data, scorecard: scorecardQ.data, log: logQ.data }
  const valve = valveSummary(conviction.status, conviction.scorecard, conviction.log, today)
  const { rows, flags } = overlayBullets(
    overlayQ.data,
    conviction.status,
    valve,
    dormant ? undefined : positionsQ.data,
    dormant ? undefined : drawdownQ.data,
  )

  const overlay = overlayQ.data
  const version = num(overlay?.version)
  const baseConfig = !isRecord(overlay?.overlay) || Object.keys(overlay.overlay).length === 0
  const found = scoreCaveat(valve, today)
  const caveat = found?.kind === 'coverage' && stillLoading(scorecardQ) ? null : found

  const flagState = (on: boolean | null) => (on === null ? 'unknown' : on ? 'on' : 'off')
  const ariaLabel = `overlay knobs vs live: ${[
    ...rows.map((r) => `${r.label} ${spoken(r.liveDisplay)} live of ${spoken(r.settingDisplay)}`),
    ...flags.map((f) => `${f.label} ${flagState(f.on)}`),
  ].join(', ')}`

  return (
    <Module
      title="Overlay"
      caption={version === null ? undefined : `v${version}`}
      drawerKey="overlay"
      span={span ?? 2}
      isLoading={stillLoading(overlayQ)}
      isError={unavailable(overlayQ)}
      errorText="overlay unavailable"
    >
      <BulletBars rows={rows} flags={flags} ariaLabel={ariaLabel} />
      <p className="module-foot">
        {baseConfig ? 'base config — no overlay' : `updated ${relativeTime(overlay?.updated_at)}`}
        {caveat !== null && (
          <span className="warn">
            {' '}
            {together(`· θ ${caveatText(caveat)}`)}
          </span>
        )}
      </p>
    </Module>
  )
}

// ------------------------------------------------------------------- mirror

/** The Mirror tile's dormant rung (BookModulesRow1), word for word, so the two never disagree on one book. */
const DORMANT_MIRROR = 'dormant · not in engine'

/**
 * Validation row 3: what the subscriber book's mirror holds against what it still has to do, as the
 * engine's last tick reported it: names in step, names still to enter, names still to exit. The ladder is
 * mirrorSummary's, the Mirror tile's too, and like the tile there is no query: the tick lives on the book
 * status.
 *
 * Only a tick that reported its lists is drawn. A book that has not ticked since the restart is awaiting its
 * first, one whose last tick failed holds sets that are old, and one with no pick source has nothing to
 * mirror; none of those may show three zeroes, so each gets its rung (the failed one the error rung, since
 * the sets are last-known and the tile calls it FAILED). A book that is not in the engine has no tick to wait
 * for and gets the tile's dormant rung, whatever else is true of it. The caption names the source book, and
 * is left off when there is none: "source #null" is a string, not a state.
 *
 * The foot dates the tick, so the face re-reads the clock (useNow) as the tile does: the status polls hand
 * back the same tick until the next one is written, and an age that only moved with a poll would stand still.
 */
export function MirrorModule({ book }: BookModuleProps) {
  useNow(CLOCK_MS)
  const s = mirrorSummary(book)
  const dormant = isDormant(book)
  const source = book.pick_source_account_id

  // A halted book has no tick by construction: "awaiting" would tell the operator to wait for one that will not come.
  const halted = s.state === 'awaiting' && book.bookStatus?.halted === true

  let emptyText = halted ? 'book halted · no mirror tick' : 'awaiting first mirror tick since restart'
  if (dormant) emptyText = DORMANT_MIRROR
  else if (s.state === 'no-source') emptyText = 'no pick source configured'

  return (
    <Module
      title="Mirror"
      caption={source == null ? undefined : `source #${source}`}
      drawerKey="mirror"
      span={3}
      isError={s.state === 'failed'}
      errorText="last mirror tick failed"
      isEmpty={dormant || s.state === 'awaiting' || s.state === 'no-source'}
      emptyText={emptyText}
    >
      <MiniFunnel
        stages={[
          { label: 'Synced', count: s.synced, tone: 'gain' },
          { label: 'Pending entry', count: s.pendingEntry, tone: 'warn' },
          { label: 'Pending exit', count: s.pendingExit, tone: 'warn' },
        ]}
        ariaLabel={`mirror: ${s.synced} synced, ${s.pendingEntry} pending entry, ${s.pendingExit} pending exit`}
      />
      <p className="module-foot">{`last tick ${relativeTime(s.asOf)}`}</p>
    </Module>
  )
}

// ---------------------------------------------------------------- inception

/**
 * Validation row 3: what this book is for. The validation book is a permanent canary on a clean-inception
 * account: it proves the platform end-to-end and never graduates to live (CleanInceptionBanner says it at
 * length). The face is the three facts that identify it, from the registry row and the book status, so it has
 * no query and no state. An overlay version the engine has not reported (the book is not in it) is a dash,
 * and so is a missing pick source; version 0 is a version (the base config), not an unknown.
 *
 * No caption: the title and "permanent canary" together are wider than the span-3 header, so the note is the
 * foot. The broker mode is the one free-text value: BookModules.css gives way to an ellipsis for a very long
 * one, and its title carries all of it.
 */
export function InceptionModule({ book }: BookModuleProps) {
  const version = num(book.bookStatus?.overlay_version)
  return (
    <Module title="Clean inception" drawerKey="inception" span={3}>
      <p className="module-row">
        <span>overlay</span>
        <b>{`v${version ?? DASH}`}</b>
      </p>
      <p className="module-row">
        <span>mode</span>
        <b title={book.broker_mode}>{book.broker_mode}</b>
      </p>
      <p className="module-row">
        <span>source</span>
        <b>{`#${book.pick_source_account_id ?? DASH}`}</b>
      </p>
      <p className="module-foot">permanent canary · proves the platform end-to-end, never graduates to live</p>
    </Module>
  )
}

// -------------------------------------------------------------- kill-entries

const KILL_COMPARE = 'per-book overlay switch · exits keep running'

/**
 * Live row 3: the book's kill-entries switch, as KillEntriesCard shows it: the BOOK's overlay
 * (`conviction_entries_enabled === false` is a kill; an overlay that says nothing is the backend's default,
 * on). It is read for a dormant book too, as the card does. The switch is restrict-only and takes effect on
 * the next cycle; exits keep running.
 *
 * It is the per-book switch and nothing else: the global setting can also kill entries, and it is not shown
 * here, so the compare line says whose switch this is rather than let "entries enabled" read as "free to
 * enter". The overlay read is the face: its loading and error rungs. An answer that is not an overlay record
 * says nothing about the switch, so it is the error rung and never the green word. A refetch that fails
 * keeps the switch state the face already has (unavailable, not a bare isError).
 *
 * The switch being on is green only on a book that is running: a dormant book (not in the engine) or a
 * halted one enters nothing whatever its switch says, so there "entries enabled" is neutral and the compare
 * line names the book's state after the switch wording. A kill stays a kill (loss tone) on any book.
 */
export function KillEntriesModule({ book }: BookModuleProps) {
  const q = useBookOverlay(book)
  const loading = stillLoading(q)

  const overlay = q.data
  const unreadable = overlay !== undefined && !isRecord(overlay)
  const killed = isRecord(overlay) && isRecord(overlay.overlay) && overlay.overlay.conviction_entries_enabled === false
  const bookState = isDormant(book) ? 'book dormant' : book.bookStatus?.halted === true ? 'book halted' : null
  const enabledTone = bookState === null ? 'gain' : 'neutral'

  return (
    <StatModule
      title="Kill-entries"
      caption="restrict-only · next cycle"
      drawerKey="kill-entries"
      span={4}
      value={killed ? 'ENTRIES KILLED' : 'entries enabled'}
      // The dash a loading tile prints is an unknown, and an unknown is not green.
      tone={loading ? 'neutral' : killed ? 'loss' : enabledTone}
      isLoading={loading}
      isError={unavailable(q) || unreadable}
      errorText="overlay unavailable"
      compare={loading ? undefined : bookState === null ? KILL_COMPARE : `${KILL_COMPARE} · ${bookState}`}
    />
  )
}

// ------------------------------------------------------------- cap rejects

/**
 * Live row 3: live-cap rejections, as a placeholder (ruling 36). No endpoint exposes them: the execution
 * router records a live-cap rejection in per-account Redis counters nothing reads back, and the signal
 * history the legacy panel reads is only fed by the manual execute / reject endpoints, so an empty feed
 * proves nothing. A count, a "0", a "none" or a green state here would be a reassuring guess on the
 * real-money containment surface, so the face is a dash that says there is no source, and it makes no
 * request at all. It reads the same on every book. The explanation lives in the drawer.
 *
 * It takes the book like every face (the page mounts them uniformly) but needs nothing from it.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function CapRejectsModule(_props: BookModuleProps) {
  return (
    <StatModule
      title="Live-cap rejections"
      caption="not yet surfaced"
      drawerKey="cap-rejects"
      span={4}
      value={DASH}
      compare="router rejections have no API source yet"
    />
  )
}

// ----------------------------------------------------------------- dormancy

/** The Live gate tile's words and tones (BookModulesRow1), so the two tiles on one page never disagree. */
const CONTAINMENT_FACE: Record<LiveGate['state'], { value: string; tone: 'gain' | 'warn' | 'neutral' }> = {
  dormant: { value: 'DORMANT', tone: 'neutral' },
  halted: { value: 'HALTED', tone: 'warn' },
  running: { value: 'RUNNING', tone: 'gain' },
}

/**
 * Live row 3: where the live book stands against the three things that contain it, as three facts: the
 * registry row is enabled or not, the feed it subscribes to (the research book's id) or none, and whether an
 * engine book was built. The word is liveGate's, the Live gate tile's too; the rows are the gates
 * themselves, so they stay true whatever the word says. From the book status alone, so there is no query.
 *
 * A halted book shows the engine's halt reason in warn tone, never behind a drawer. The engine's reason has
 * no length limit: the tile shows two lines of it (BookModules.css) and its title carries the rest.
 */
export function DormancyModule({ book }: BookModuleProps) {
  const g = liveGate(book)
  const face = CONTAINMENT_FACE[g.state]
  const { registry, feed, engine } = g.gates

  let reason: ReactNode = null
  if (g.state === 'halted') {
    reason = g.reason ? (
      <span className="warn" title={g.reason}>
        {g.reason}
      </span>
    ) : (
      <span className="warn">halted (no reason reported)</span>
    )
  }

  return (
    <StatModule
      title="Containment"
      drawerKey="dormancy"
      span={4}
      value={face.value}
      tone={face.tone}
      compare={reason}
      footer={
        <>
          <p className="module-row">
            <span>registry</span>
            <b>{registry ? 'enabled' : 'disabled'}</b>
          </p>
          <p className="module-row">
            <span>feed</span>
            <b>{feed ? `#${book.pick_source_account_id}` : 'none'}</b>
          </p>
          <p className="module-row">
            <span>engine book</span>
            <b>{engine ? 'built' : 'not built'}</b>
          </p>
        </>
      }
    />
  )
}
